import { useEffect, useMemo, useState } from "react";
import { Plus, Search, X } from "lucide-react";
import type { Project, Workspace } from "@racler/protocol";
import { Header } from "../components/Header.tsx";
import { RecentChatsSection } from "../components/RecentChatsSection.tsx";
import { RecentProjectsSection } from "../components/RecentProjectsSection.tsx";
import { UpdatesNotice } from "../components/UpdatesNotice.tsx";
import { api, type User } from "../lib/api.ts";
import { assembleDashboardProjects, filterProjects, sortChats } from "../lib/homeData.ts";
import { loadRecent } from "../lib/recent.ts";
import { useRelay } from "../lib/relayContext.tsx";
import { navigate } from "../lib/router.ts";
import { useAsync } from "../lib/useAsync.ts";
import { useMachines } from "../lib/useMachines.ts";

const PAGE_SIZE = 8;

export function HomePage({ user, onLogout }: { user: User; onLogout: () => void }) {
  const { machines, error: machinesError } = useMachines();
  const recentChatsAsync = useAsync(() => api.recentChats({ limit: 100 }), "recent-chats-100");
  const relay = useRelay();

  const [machineData, setMachineData] = useState<
    Map<string, { projects: Project[]; workspaces: Workspace[] }>
  >(new Map());

  useEffect(() => {
    if (!machines) return;
    const onlineMachines = machines.filter((m) => m.online);
    if (onlineMachines.length === 0) return;

    let cancelled = false;
    void Promise.all(
      onlineMachines.map((m) =>
        relay
          .server("system/machine.subscribe", { machineId: m.id })
          .then(() =>
            Promise.all([
              relay.machine(m.id, "system/projects.list", {}),
              relay.machine(m.id, "system/workspaces.list", {}),
            ]),
          )
          .then(([p, w]) => ({
            machineId: m.id,
            projects: p.projects,
            workspaces: w.workspaces,
          }))
          .catch(() => null),
      ),
    ).then((results) => {
      if (cancelled) return;
      const nextMap = new Map<string, { projects: Project[]; workspaces: Workspace[] }>();
      for (const r of results) {
        if (r) nextMap.set(r.machineId, { projects: r.projects, workspaces: r.workspaces });
      }
      setMachineData(nextMap);
    });

    return () => {
      cancelled = true;
    };
  }, [relay, machines]);

  const recent = useMemo(() => loadRecent(), []);

  const allChats = useMemo(() => {
    return sortChats(recentChatsAsync.data?.chats ?? []);
  }, [recentChatsAsync.data?.chats]);

  const allProjects = useMemo(() => {
    return assembleDashboardProjects({
      machines,
      machineData,
      recent,
      chats: allChats,
    });
  }, [machines, machineData, recent, allChats]);

  const projectNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of allProjects) {
      map.set(`${p.machineId}:${p.projectId}`, p.name);
    }
    return map;
  }, [allProjects]);

  const [searchQuery, setSearchQuery] = useState("");
  const [projectPage, setProjectPage] = useState(1);
  const [chatPage, setChatPage] = useState(1);

  const trimmedQuery = searchQuery.trim();
  const searchChatsAsync = useAsync(
    () => (trimmedQuery ? api.recentChats({ search: trimmedQuery }) : Promise.resolve({ chats: [] })),
    `search-chats:${trimmedQuery}`,
  );

  useEffect(() => {
    setProjectPage(1);
    setChatPage(1);
  }, [searchQuery]);

  const filteredProjects = useMemo(() => {
    return filterProjects(allProjects, searchQuery);
  }, [allProjects, searchQuery]);

  const filteredChats = useMemo(() => {
    if (!trimmedQuery) return allChats;
    return sortChats(searchChatsAsync.data?.chats ?? []);
  }, [allChats, trimmedQuery, searchChatsAsync.data?.chats]);

  const onNewProject = () => {
    const online = machines?.find((m) => m.online);
    if (online) {
      navigate(`/settings/projects?add=${online.id}&mode=create`);
    } else {
      navigate("/settings/projects?mode=create");
    }
  };

  return (
    <div className="page">
      <Header user={user} onLogout={onLogout} />
      <main className="home" data-testid="home">
        {machinesError && <p className="error">{machinesError}</p>}

        <header className="home-hero">
          <div className="home-hero-content">
            <h1 className="home-greeting">
              Welcome back, <span className="home-user-name">{user.displayName}</span>
            </h1>
            <p className="home-subline">Pick up where you left off.</p>
          </div>
          <div className="home-hero-actions">
            <button
              className="primary home-btn-new-project"
              onClick={onNewProject}
              data-testid="qa-create-project"
              title="Create a new Project"
            >
              <Plus size={15} aria-hidden /> New Project
            </button>
          </div>
        </header>

        <UpdatesNotice />

        <section className="home-search-section" aria-label="Global search">
          <div className="home-search-bar">
            <Search size={16} className="home-search-icon" aria-hidden />
            <input
              type="text"
              className="home-search-input"
              placeholder="Search projects and chats..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="home-search"
              aria-label="Search projects and chats"
            />
            {searchQuery && (
              <button
                type="button"
                className="home-search-clear"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search query"
                title="Clear search"
              >
                <X size={14} aria-hidden />
              </button>
            )}
          </div>
        </section>

        <div className="home-grid">
          <RecentProjectsSection
            allProjects={allProjects}
            filteredProjects={filteredProjects}
            page={projectPage}
            pageSize={PAGE_SIZE}
            onPageChange={setProjectPage}
            onNewProject={onNewProject}
          />
          <RecentChatsSection
            allChats={allChats}
            filteredChats={filteredChats}
            projectNames={projectNames}
            page={chatPage}
            pageSize={PAGE_SIZE}
            onPageChange={setChatPage}
          />
        </div>
      </main>
    </div>
  );
}
